import { useEffect, useRef, useState } from "react";
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import PrimaryRightIconFill from "../CommonButtons/PrimaryRightIconFill";
import SingleSelectDropDownComponent from "../CommonDropdown/SingleSelectDropdown";
import TextInputField from "../CommonInputFields/TextInputField";
import AmountInputField from "../CommonInputFields/AmountInputField";
import TitleSubtitlePair from "../TitleSubtitlePair";
import ICONS from "../../../assets/Icons";
import SVGComponent from "../SVGComponent";
import { useSelector } from "react-redux";
import useCategories from "../../../utils/useCategories";

function CategoryPill({ id }) {
  const categories = useSelector(
    (state) => state.sharedResources?.categoriesData?.categories,
  );
  const categoryMapper = useCategories();
  const categoryIcon = categoryMapper(id);
  const categoryName = categories.find((x) => x.id === id)?.name ?? "";
  return (
    <div
      className="bg-text-primary rounded-l-border-radius-sm py-padding-xs px-padding-sm h-fit flex-shrink-0"
      title={categoryName}
    >
      <SVGComponent
        alt={categoryName}
        url={categoryIcon?.url}
        color={categoryIcon?.colour}
        height="var(--icon-sm)"
        width="var(--icon-sm)"
      />
    </div>
  );
}

const CommonRequestDetailsCard = ({
  categoryId,
  selectedSourceAccount,
  selectedPaymentMethod,
  amount,
  comment,
  paymentMethodOptions,
  onSourceAccountChange,
  onPaymentMethodChange,
  onAmountChange,
  onCommentChange,
  isLoading,
  error,
  entries,
  onAdornmentClick,
}) => {
  const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false);
  const commentInputRef = useRef(null);
  const prevCommentRef = useRef(comment);

  useEffect(() => {
    const wasFilled = prevCommentRef.current && prevCommentRef.current !== "";
    prevCommentRef.current = comment;
    if (wasFilled && !comment && commentInputRef.current) {
      commentInputRef.current.focus();
      const id = setTimeout(
        () => commentInputRef.current?.blur(),
        GlobalConfiguration.lengthCheckZero,
      );
      return () => clearTimeout(id);
    }
  }, [comment]);

  const rowEntries = Array.isArray(entries?.[0]) ? entries[0] : [];

  return (
    <div className=" w-full Qsmd:max-w-max-content-width  mx-auto rounded-border-radius-md bg-primary-card   flex flex-col ">
      <div className="pl-padding-sm  Qsmd:pl-padding-sm pt-padding-md Qsmd:pt-padding-sm ">
        <div className="flex flex-col Qsmd:flex-row justify-between Qsmd:gap-gap-base gap-top_md w-full border-b  border-primary-bg pb-padding-sm">
          {rowEntries.map((entry, index) => (
            <div
              key={entry.subText ?? index}
              className={`flex w-full
                 ${entry.type !== "cta" && "pl-padding-sm  Qsmd:mt-margin-sm Qsmd:mb-margin-sm "}
                  flex-col gap-gap-xs flex-1  Qsmd:max-w-request-approval-card-max-width
                  ${
                    index > 0 && index !== 3
                      ? `Qsmd:border-l ${index === 2 && "Qsmd:border-r Qsmd:border-primary-bg"}
                   Qsmd:border-primary-bg Qsmd:pl-padding-md`
                      : index === 3
                        ? "Qsmd:pl-padding-sm pr-padding-base"
                        : "Qsmd:pr-padding-md"
                  }`}
            >
              <div className="flex flex-row justify-between gap-gap-md h-auto items-center">
                <TitleSubtitlePair
                  enableTicker
                  hoverOption={entry.type === "cta" ? "white" : undefined}
                  showArrow={entry.type === "cta"}
                  isDisabled={entry.isDisabled ?? false}
                  onClick={entry?.onClick}
                  title={entry.text ?? ""}
                  subtitle={entry.subText ?? ""}
                  titleColor={entry.textColor ?? "text-primary"}
                  variant="sm"
                  isBold={entry.isBold ?? false}
                />
                {index === 3 && <CategoryPill id={categoryId} />}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col Qsmd:flex-row Qsmd:gap-gap-md Qsmd:items-end w-full px-padding-md">
        <div
          className={`flex flex-col gap-gap-xs flex-1 pt-padding-md  Qsmd:pb-padding-md rounded-border-radius-md `}
        >
          <p
            className={`text-font-xs  font-normal ${error?.sourceAccount ? "text-failed" : "text-text-secondary"}`}
          >
            Source account
          </p>

          <PrimaryRightIconFill
            btnText={
              selectedSourceAccount
                ? selectedSourceAccount
                : "Select owner account"
            }
            enabledBgColor="var(--primary-nav-act)"
            isDisabled={false}
            handleFunction={onSourceAccountChange}
            iconToBeAlign="right"
            enabledTextColor={"var(--text-primary)"}
            icon={ICONS?.ARROW_RIGHT_WHITE}
            fullWidthRequired={true}
            hoverOption="white"
            imgColor={"var(--text-primary)"}
            isNeedOutline={error?.sourceAccount}
            outlineColor="var(--failed)"
          />
        </div>

        <div
          className={`flex flex-col gap-gap-xs flex-1 pt-padding-md Qsmd:pb-padding-md rounded-border-radius-md `}
        >
          <p
            className={`text-font-xs  font-normal ${error?.paymentMethod ? "text-failed" : "text-text-secondary"}`}
          >
            Payment method
          </p>
          <SingleSelectDropDownComponent
            options={paymentMethodOptions}
            selected={selectedPaymentMethod}
            setSelected={onPaymentMethodChange}
            isOpen={isPaymentDropdownOpen}
            onOpen={() => setIsPaymentDropdownOpen(true)}
            onClose={() => setIsPaymentDropdownOpen(false)}
            placeholder="Select type"
            searchPlaceholder="Search type"
            showSearch={false}
            type="small"
            disabled={true}
            dropdownType="PaymentMethod"
          />
        </div>

        <div className="flex flex-col gap-gap-xs flex-1 pt-padding-md Qsmd:pb-padding-md">
          <AmountInputField
            upperLimit={GlobalConfiguration.maxAllowedAmount}
            needsColor
            name="amount"
            variant={
              error?.amount || error.requested_amount ? "error" : "default"
            }
            placeholder="Approved amount"
            value={amount}
            onInput={(val) => onAmountChange(val)}
            fixedDecimalScale={true}
            bgColor="var(--primary-card)"
            fgColor="var(--text-primary)"
            fontKind="small"
            isDisabled={isLoading}
            isFullWidth={true}
            onAdornmentClick={
              error?.amount || error.requested_amount
                ? (e) => {
                    onAdornmentClick(e);
                  }
                : undefined
            }
          />
        </div>

        <div className="flex flex-col gap-gap-xs flex-1 pt-padding-md pb-padding-md">
          <TextInputField
            ref={commentInputRef}
            variant="default"
            placeholder="Comment (optional)"
            value={comment}
            onInput={(e) => onCommentChange(e.target.value)}
            bgColor="var(--primary-card)"
            fgColor="var(--text-primary)"
            fontKind="small"
            isDisabled={isLoading}
            isFullWidth={true}
            adornment={""}
          />
        </div>
      </div>
    </div>
  );
};

export default CommonRequestDetailsCard;
